/**
 * Small global behaviors: filter forms submit when a choice changes,
 * menus close on Escape or an outside click, color thumbnails on product
 * cards swap the card photo, rows of cards scroll sideways, and the
 * "recently viewed" row fills itself in.
 */
const showCardColor = (swatch) => {
  const card = swatch.closest('[data-product-card]');
  const image = card && card.querySelector('.product-card__image:not(.product-card__image--alternate)');
  if (!image || !swatch.dataset.src) return;
  image.removeAttribute('srcset');
  image.src = swatch.dataset.src;
  const alternate = card.querySelector('.product-card__image--alternate');
  if (alternate) alternate.hidden = true;
  card.querySelectorAll('[data-card-swatch]').forEach((item) => {
    item.setAttribute('aria-pressed', String(item === swatch));
  });
};

document.addEventListener('click', (event) => {
  const swatch = event.target.closest('[data-card-swatch]');
  if (swatch) showCardColor(swatch);
});

document.addEventListener('mouseover', (event) => {
  const swatch = event.target.closest('[data-card-swatch]');
  if (swatch) showCardColor(swatch);
});

document.addEventListener('change', (event) => {
  const form = event.target.closest('form[data-auto-submit]');
  if (!form) return;
  if (event.target.matches('input[type="number"], input[type="text"]')) return;
  form.submit();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('details[data-close-on-escape][open]').forEach((details) => {
    details.open = false;
    const summary = details.querySelector('summary');
    if (summary) summary.focus();
  });
});

/*
 * Header panels (menus and search): only one is open at a time, and they
 * close when the visitor clicks elsewhere.
 */
document.addEventListener('click', (event) => {
  document.querySelectorAll('details[data-menu][open]').forEach((details) => {
    if (!details.contains(event.target)) details.open = false;
  });
});

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (details.matches('[data-menu]')) {
      document.querySelectorAll('details[data-menu][open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
      const field = details.querySelector('input[type="search"]');
      if (field) field.focus();
    }
    if (details.matches('.header__drawer')) document.documentElement.classList.add('has-open-drawer');
  },
  true
);

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target.matches && event.target.matches('.header__drawer') && !event.target.open) {
      document.documentElement.classList.remove('has-open-drawer');
    }
  },
  true
);

/* A row of cards that scrolls sideways, with previous and next buttons. */
if (!customElements.get('scroll-row')) {
  class ScrollRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroll-track]');
      this.previous = this.querySelector('[data-scroll-previous]');
      this.next = this.querySelector('[data-scroll-next]');
      if (!this.track) return;

      const step = (direction) => {
        this.track.scrollBy({ left: direction * this.track.clientWidth * 0.85, behavior: 'smooth' });
      };
      if (this.previous) this.previous.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));

      let frame = null;
      this.update = () => {
        frame = null;
        const last = this.track.scrollWidth - this.track.clientWidth;
        if (this.previous) this.previous.disabled = this.track.scrollLeft <= 1;
        if (this.next) this.next.disabled = this.track.scrollLeft >= last - 1;
        this.classList.toggle('scroll-row--static', last <= 1);
      };
      this.track.addEventListener('scroll', () => {
        if (!frame) frame = requestAnimationFrame(this.update);
      });
      window.addEventListener('resize', this.update);
      this.update();
    }
  }

  customElements.define('scroll-row', ScrollRow);
}

/*
 * Recently viewed frames. Product pages add their handle to a short list
 * kept in the browser; the section then asks the store for each card.
 */
const RECENT_KEY = 'yoroi:recent';

const readRecent = () => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
  } catch (error) {
    return [];
  }
};

const currentProduct = document.querySelector('product-info[data-handle]');
if (currentProduct) {
  const handle = currentProduct.dataset.handle;
  const list = [handle, ...readRecent().filter((item) => item !== handle)].slice(0, 12);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch (error) {
    /* Storage can be unavailable in private windows; the row just stays hidden. */
  }
}

if (!customElements.get('recently-viewed')) {
  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const limit = Number(this.dataset.limit) || 6;
      const root = this.dataset.root || '/';
      const handles = readRecent()
        .filter((handle) => handle !== this.dataset.current)
        .slice(0, limit);
      const list = this.querySelector('[data-recent-list]');
      if (!list || handles.length === 0) return;

      const cards = await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=product-card-item`);
            if (!response.ok) return '';
            const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = markup.querySelector('[data-product-card]');
            return card ? card.outerHTML : '';
          } catch (error) {
            return '';
          }
        })
      );

      const found = cards.filter(Boolean);
      if (found.length === 0) return;
      list.innerHTML = found.map((card) => `<li>${card}</li>`).join('');
      this.hidden = false;
      const row = this.querySelector('scroll-row');
      if (row && row.update) row.update();
      document.dispatchEvent(new CustomEvent('yoroi:content-added', { detail: { root: this } }));
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
}

/* Banner slideshow: previous/next buttons and the slide counter. */
if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slides]');
      this.current = this.querySelector('[data-slide-current]');
      if (!this.track) return;

      const step = (direction) => {
        const width = this.track.clientWidth;
        const last = this.track.scrollWidth - width;
        let target = this.track.scrollLeft + direction * width;
        if (target > last + 1) target = 0;
        if (target < -1) target = last;
        this.track.scrollTo({ left: target, behavior: 'smooth' });
      };

      const previous = this.querySelector('[data-slide-previous]');
      const next = this.querySelector('[data-slide-next]');
      if (previous) previous.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));

      let frame = null;
      this.track.addEventListener('scroll', () => {
        if (frame || !this.current) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          this.current.textContent = String(Math.round(this.track.scrollLeft / this.track.clientWidth) + 1);
        });
      });
    }
  }

  customElements.define('slide-show', SlideShow);
}

/*
 * Search results while typing. The store's predictive search is asked
 * first. Where it is not offered (it answers with an error for some
 * languages), the ordinary search is asked instead, so suggestions work
 * in every language. Both return the "search-suggest" section.
 */
if (!customElements.get('search-suggest')) {
  let predictiveWorks = true;

  class SearchSuggest extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-suggest-input]');
      this.results = this.querySelector('[data-suggest-results]');
      if (!this.input || !this.results) return;
      let timer = null;
      this.input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => this.search(), 250);
      });
    }

    address(terms, predictive) {
      const query = predictive
        ? { q: terms, 'resources[type]': 'product', 'resources[limit]': '6', section_id: 'search-suggest' }
        : { q: terms, type: 'product', 'options[prefix]': 'last', section_id: 'search-suggest' };
      const base = predictive ? this.dataset.predictiveUrl : this.dataset.url;
      return `${base}?${new URLSearchParams(query)}`;
    }

    async load(terms, predictive, signal) {
      const response = await fetch(this.address(terms, predictive), { signal });
      if (!response.ok) return null;
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const found = markup.querySelector('[data-suggest]');
      return found ? found.outerHTML : null;
    }

    async search() {
      const terms = this.input.value.trim();
      const ascii = /^[\x00-\x7f]*$/.test(terms);
      if (terms.length < (ascii ? 2 : 1)) {
        this.results.innerHTML = '';
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      try {
        let markup = null;
        if (predictiveWorks && this.dataset.predictiveUrl) {
          markup = await this.load(terms, true, signal);
          if (markup === null) predictiveWorks = false;
        }
        if (markup === null) markup = await this.load(terms, false, signal);
        if (!signal.aborted) this.results.innerHTML = markup || '';
      } catch (error) {
        /* A newer search replaced this one, or the network dropped: leave the field usable. */
      }
    }
  }

  customElements.define('search-suggest', SearchSuggest);
}

/*
 * Keyboard use of the menus. Tab stays inside the open mobile menu, and a
 * desktop panel closes once focus has moved out of it.
 */
const focusable = (container) =>
  Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element.getClientRects().length > 0);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab') return;
  const drawer = document.querySelector('details.header__drawer[open]');
  if (!drawer || !drawer.contains(document.activeElement)) return;
  const items = focusable(drawer);
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

document.addEventListener('focusout', (event) => {
  const details = event.target.closest && event.target.closest('details[data-menu][open]');
  if (!details || !event.relatedTarget || details.contains(event.relatedTarget)) return;
  details.open = false;
});

/**
 * Rows that scroll sideways (worn photos on a phone, rows of cards) can be
 * reached with the keyboard when they hold no links or buttons of their own.
 */
const markScrollRegions = () => {
  document.querySelectorAll('.lookbook, [data-scroll-track]').forEach((region) => {
    const scrolls = region.scrollWidth > region.clientWidth + 1;
    if (scrolls && focusable(region).length === 0) {
      region.tabIndex = 0;
    } else if (region.tabIndex === 0) {
      region.removeAttribute('tabindex');
    }
  });
};

markScrollRegions();
window.addEventListener('load', markScrollRegions);
window.addEventListener('resize', markScrollRegions);
